import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiHome, FiUsers, FiFileText, FiSettings, FiDownload, FiMenu, FiX, FiBriefcase, FiShield, FiBook, FiTarget, FiFilePlus } from 'react-icons/fi';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getNavigationItems = () => {
    const baseItems = [
      { name: 'Dashboard', href: '/dashboard', icon: FiHome, current: location.pathname === '/dashboard' },
    ];

    if (user?.role === 'hr_clerk') {
      return [
        ...baseItems,
        { name: 'Drafts', href: '/drafts', icon: FiFilePlus, current: location.pathname === '/drafts' },
        { name: 'New Submissions', href: '/submissions/new', icon: FiFileText, current: location.pathname === '/submissions/new' },
        { name: 'Under Review', href: '/submissions/under-review', icon: FiUsers, current: location.pathname === '/submissions/under-review' },
        { name: 'Rework Requested', href: '/submissions/rework-requested', icon: FiFileText, current: location.pathname === '/submissions/rework-requested' },
        { name: 'Profiles Verified', href: '/submissions/verified', icon: FiShield, current: location.pathname === '/submissions/verified' },
        { name: 'Available for Hire', href: '/submissions/available', icon: FiBriefcase, current: location.pathname === '/submissions/available' },
        { name: 'Hired', href: '/submissions/hired', icon: FiUsers, current: location.pathname === '/submissions/hired' },
        { name: 'Rejected', href: '/submissions/rejected', icon: FiX, current: location.pathname === '/submissions/rejected' },
        { name: 'Terminated', href: '/submissions/terminated', icon: FiX, current: location.pathname === '/submissions/terminated' },
        { name: 'Blacklisted', href: '/submissions/blacklisted', icon: FiX, current: location.pathname === '/submissions/blacklisted' },
        { name: 'Documentation', href: '/documentation', icon: FiBook, current: location.pathname === '/documentation' },
      ];
    }

    if (user?.role === 'manager') {
      return [
        ...baseItems,
        { name: 'Available for Hire', href: '/submissions/available', icon: FiBriefcase, current: location.pathname === '/submissions/available' },
        { name: 'Documentation', href: '/documentation', icon: FiBook, current: location.pathname === '/documentation' },
      ];
    }

    if (user?.role === 'admin') {
      return [
        ...baseItems,
        { name: 'Drafts', href: '/drafts', icon: FiFilePlus, current: location.pathname === '/drafts' },
        { name: 'New Submissions', href: '/submissions/new', icon: FiFileText, current: location.pathname === '/submissions/new' },
        { name: 'Under Review', href: '/submissions/under-review', icon: FiUsers, current: location.pathname === '/submissions/under-review' },
        { name: 'Rework Requested', href: '/submissions/rework-requested', icon: FiFileText, current: location.pathname === '/submissions/rework-requested' },
        { name: 'Profiles Verified', href: '/submissions/verified', icon: FiShield, current: location.pathname === '/submissions/verified' },
        { name: 'Available for Hire', href: '/submissions/available', icon: FiBriefcase, current: location.pathname === '/submissions/available' },
        { name: 'Hired', href: '/submissions/hired', icon: FiUsers, current: location.pathname === '/submissions/hired' },
        { name: 'Rejected', href: '/submissions/rejected', icon: FiX, current: location.pathname === '/submissions/rejected' },
        { name: 'Terminated', href: '/submissions/terminated', icon: FiX, current: location.pathname === '/submissions/terminated' },
        { name: 'Blacklisted', href: '/submissions/blacklisted', icon: FiX, current: location.pathname === '/submissions/blacklisted' },
        { name: 'Audit Trail', href: '/admin/audit', icon: FiShield, current: location.pathname === '/admin/audit' },
        { name: 'Export Data', href: '/export', icon: FiDownload, current: location.pathname === '/export' },
        { name: 'Positions', href: '/admin/positions', icon: FiTarget, current: location.pathname === '/admin/positions' },
        { name: 'Settings', href: '/settings', icon: FiSettings, current: location.pathname === '/settings' },
        { name: 'Documentation', href: '/documentation', icon: FiBook, current: location.pathname === '/documentation' },
      ];
    }

    if (user?.role === 'viewer') {
      return [
        ...baseItems,
        { name: 'Drafts', href: '/drafts', icon: FiFilePlus, current: location.pathname === '/drafts' },
        { name: 'New Submissions', href: '/submissions/new', icon: FiFileText, current: location.pathname === '/submissions/new' },
        { name: 'Under Review', href: '/submissions/under-review', icon: FiUsers, current: location.pathname === '/submissions/under-review' },
        { name: 'Rework Requested', href: '/submissions/rework-requested', icon: FiFileText, current: location.pathname === '/submissions/rework-requested' },
        { name: 'Profiles Verified', href: '/submissions/verified', icon: FiShield, current: location.pathname === '/submissions/verified' },
        { name: 'Available for Hire', href: '/submissions/available', icon: FiBriefcase, current: location.pathname === '/submissions/available' },
        { name: 'Hired', href: '/submissions/hired', icon: FiUsers, current: location.pathname === '/submissions/hired' },
        { name: 'Rejected', href: '/submissions/rejected', icon: FiX, current: location.pathname === '/submissions/rejected' },
        { name: 'Terminated', href: '/submissions/terminated', icon: FiX, current: location.pathname === '/submissions/terminated' },
        { name: 'Blacklisted', href: '/submissions/blacklisted', icon: FiX, current: location.pathname === '/submissions/blacklisted' },
        { name: 'Audit Trail', href: '/admin/audit', icon: FiShield, current: location.pathname === '/admin/audit' },
        { name: 'Positions', href: '/admin/positions', icon: FiTarget, current: location.pathname === '/admin/positions' },
        { name: 'Documentation', href: '/documentation', icon: FiBook, current: location.pathname === '/documentation' },
      ];
    }

    // Default for candidates
    return [
      { name: 'My Application', href: '/my-application', icon: FiFileText, current: location.pathname === '/my-application' },
    ];
  };

  const navigationItems = getNavigationItems();

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-accent-100 text-accent-800 border border-accent-200';
      case 'hr_clerk': return 'bg-brand-100 text-brand-800 border border-brand-200';
      case 'manager': return 'bg-success-100 text-success-800 border border-success-200';
      case 'viewer': return 'bg-gray-100 text-gray-700 border border-gray-300';
      default: return 'bg-primary-100 text-primary-800 border border-primary-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100">
      <div className="flex">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-primary-900 bg-opacity-50 lg:hidden backdrop-blur-sm z-40" onClick={() => setSidebarOpen(false)}></div>
        )}
        
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'fixed inset-y-0 left-0 z-50' : 'hidden'} lg:sticky lg:top-0 lg:flex lg:z-auto flex flex-col w-72 bg-white shadow-xl h-screen max-h-screen border-r border-primary-200/50`}>
            <div className="flex items-center justify-between flex-shrink-0 px-6 py-8 bg-gradient-to-r from-primary-100 to-primary-50 border-b border-primary-200/50">
              <Link to="/dashboard" className="flex items-center space-x-3">
                <img 
                  src="/img/logo.png" 
                  alt="Trigon Logo" 
                  className="h-12 w-16 object-contain"
                />
                <div className="text-primary-900">
                  <div className="text-xl font-bold">Trigon</div>
                  <div className="text-sm text-primary-600">HR Portal</div>
                </div>
              </Link>
              <button
                className="lg:hidden text-primary-600 hover:text-brand-600 transition-colors"
                onClick={() => setSidebarOpen(false)}
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
            
            <nav className="flex-1 overflow-y-auto px-6 py-6 space-y-2 scrollbar-hide">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      item.current
                        ? 'sidebar-nav-item-active'
                        : 'sidebar-nav-item-inactive'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
            
            {/* User info */}
            {user && (
              <div className="flex-shrink-0 p-6 border-t border-primary-200/50 bg-gradient-to-r from-primary-50 to-white">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-full flex items-center justify-center shadow-soft">
                      <span className="text-sm font-semibold text-white">
                        {user.firstName?.charAt(0) || user.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-semibold text-primary-900 truncate">
                      {user.firstName || ''} {user.lastName || ''}
                    </p>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(user.role || 'candidate')}`}>
                      {(user.role || 'candidate').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="w-full btn-outline text-xs py-2"
                >
                  Sign Out
                </button>
              </div>
            )}
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col lg:ml-0">
          {/* Mobile header */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between bg-white/95 backdrop-blur-sm shadow-soft border-b border-primary-200/50 px-6 py-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="text-primary-600 hover:text-brand-600 transition-colors"
              >
                <FiMenu className="h-6 w-6" />
              </button>
              <div className="flex items-center space-x-2">
                <img 
                  src="/img/logo.png" 
                  alt="Trigon Logo" 
                  className="h-8 w-10 object-contain"
                />
                <h1 className="text-lg font-bold text-primary-900">Trigon HR</h1>
              </div>
              <div className="w-6"></div>
            </div>
          </div>

          <main className="flex-1 px-6 py-8 lg:px-12 lg:py-10">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
